// Production (Arps + shared reservoir + lift), gushers, storage, transport and sales (DESIGN §5.5, §6.3, §3.8).
import { allWells } from './indexes.js'; // perf: per-day well list (same order as Object.values)
import { chance, range } from '../core/rng.js';
import { dayToDate } from '../core/time.js';
import { spotPrice, netPriceMult } from '../economy/market.js';
import { eqDef, fieldDef, regionDef, stat, transportStats, unitPrice, eraDollars, isAvailable, fieldTransports, barrelDef, yearOf, yf, defsByCat } from './defs.js';
import { reservoirsAt } from './geo.js';
import { startTimer, cancelTimer } from './timers.js';
import { activeRigCrew } from './crew.js';
import { tankFireMult } from './safety.js';
import { maybeStormWarning, stormMult, startTankFire } from './tankfire.js';
import { clamp, notify, log, spend, earn, fmt$, book } from './util.js';
import { addItem, stockQty, takeStock, addStock } from '../core/state.js';
import { opexM2, reservoirPressure, rateMult, gasCapOnFirstProduction, condensateMult, noteCondensate, flareDay, evaporation, revealReservoirPressure } from './prodm2.js';
// M2 economy hooks (sim/m2/*): prorationing & hot oil, severance/partner share, war-risk transport, royalty schedules, closures
import { prorate } from './m2/proration.js';
import { onSale, deliveredMult, transportMult, regionFreightMult } from './m2/index.js';
import { severanceFor } from './m2/taxes.js';
import { regionBlock } from './m2/politics.js';
import { scheduledRoyalty } from '../data/m2/regions.js';
// M3 econ hooks (sim/m3/*): fiscal take, route freight, equity barrels, port rule (all inert before 1946)
import { m3RoyaltyRate } from './m3/fiscal.js';
import { m3RegionShipping, fieldHasPort } from './m3/routes.js';
import { equityBbl } from './m3/nationalize.js';
import { onSaleM3, saleBlocked } from './m3/econtick.js';
import { m3ProdCap, platformStorage, platformStorageByField, offshoreWellOpexMult } from './m3/platforms.js'; // M3 ops: platform process caps, export modes, FPSO/GBS storage
import { m3TransportBlocked, m3TariffMult, transportBlockReason } from './m3/megaprojects.js'; // M3 ops: megaproject outlets (TAPS), storm downtime, consortium tariffs
import { m3QiMult } from './m3/offshore.js'; // M3 ops: horizontal laterals
import { shaleRate, shaleProduced, shaleVerticalQiMult } from './m4/shale.js'; // M4-A: shale type curves, local drainage, streams (prod.shale only, ≥ 2000)
import { m4FieldSpot } from './m4/marketPrices.js'; // M4-B: hub prices & takeaway differentials (identity before 2000)
import { m4Overflow } from './m4/marketNegative.js'; // M4-B: negative-price forced sale / Cushing lease (inert before 2000)
import { worldQuality, worldSaleBlocked, worldOnSale } from './m4/worldHooks.js'; // M4-C: sanctions/capital-control prices, diluent & upgrader rules (inert before 2000)

const OPEX_KEYS = [[1860, 0.5], [1930, 3], [1980, 150], [2020, 400]];
export function opexPerWell(year) {
  if (year >= 1901) return opexM2(year); // M2_SPEC §15: 1901 $0.78 → 1930 $3 → 1945 $4
  const k = OPEX_KEYS;
  if (year <= k[0][0]) return k[0][1];
  for (let i = 1; i < k.length; i++) if (year <= k[i][0]) return k[i - 1][1] + (k[i][1] - k[i - 1][1]) * (year - k[i - 1][0]) / (k[i][0] - k[i - 1][0]);
  return k[k.length - 1][1];
}

export const LIFT_THRESHOLD = 0.55;
// Independent carters you can always hire per field before employing your own teamsters.
export const CONTRACT_TEAMSTERS = 1;
export const BARREL_REFUND = 0.6;
// Pioneer cable-tool wells were notorious for flush production that fell off within months.
export const PIONEER_DI_MULT = 1.5;

export function resState(state, well) {
  return well.reservoirId ? state.fields[well.fieldId].reservoirs[well.reservoirId] : null;
}

export function startProduction(state, well) {
  const fdef = fieldDef(well.fieldId);
  const plot = state.fields[well.fieldId].plots[well.plot];
  const r = reservoirsAt(fdef, plot).find((x) => x.id === well.reservoirId);
  let qi;
  if (well.gusher) qi = well.gusher.qiAfter;
  else {
    const [lo, hi] = r?.qiRange ?? [5, 30];
    const roll = plot.truth.qiRoll ?? 0.5;
    const pen = r ? clamp(well.payFt / r.thick, 0, 1) : 0.5;
    const rig = well.rigId ? state.rigs[well.rigId] : null;
    const driller = rig ? activeRigCrew(state, rig).find((c) => c.role === 'driller') : null;
    const compSkill = 0.9 + 0.2 * ((driller?.skill ?? 50) / 100);
    const edgeF = 1 - 0.35 * clamp(plot.truth.edge ?? 0, 0, 1);
    qi = (lo + (hi - lo) * roll) * (0.3 + 0.7 * pen) * compSkill * edgeF;
  }
  qi *= well.stim ?? 1;
  if (well.horizontal) qi *= m3QiMult(state, well); // M3 ops: horizontal lateral
  if (r?.unconventional) qi *= shaleVerticalQiMult(state, well, r); // M4-A: a vertical well in a shale bench (≥ 2000)
  well.stim = null;
  well.status = 'PRODUCING';
  well.prod = { qi: Math.round(qi * 10) / 10, di: (r?.di ?? 0.6) * (state.eraId === 'pioneer' ? PIONEER_DI_MULT : 1), b: r?.b ?? 0.5, startDay: state.day, age: 0, rate: 0, cum: well.prod?.cum ?? 0, needsLift: false, shutIn: false, forcedUntil: 0 };
  const res = resState(state, well);
  if (res && yf(state) >= 1901) { gasCapOnFirstProduction(state, well, res); revealReservoirPressure(state, well); }
  const rate = res ? flowRate(well, res) : qi;
  notify(state, 'strike', { wellId: well.id, fieldId: well.fieldId, completed: true, text: `${well.name} completed: flowing ~${Math.round(rate)} bbl/d.` });
}

function flowRate(well, res) {
  const p = well.prod;
  const t = p.age / 365;
  const arps = p.qi / Math.pow(1 + p.b * p.di * t, 1 / p.b);
  const shared = res.initial > 0 ? Math.sqrt(Math.max(0, res.remaining) / res.initial) : 0;
  return arps * shared;
}

export function tankCapacity(state, fieldId) {
  let cap = 0;
  for (const it of Object.values(state.items)) {
    if (it.loc.type === 'field' && it.loc.id === fieldId && eqDef(it.defId)?.cat === 'storage') cap += stat(eqDef(it.defId), 'capacityBbl', 0);
  }
  return cap;
}

// Tank capacity of every field in one pass over the items (tickProduction runs this daily for ~30 fields).
export function tankCapacities(state) {
  const out = {};
  for (const it of Object.values(state.items)) {
    if (it.loc.type !== 'field') continue;
    const d = eqDef(it.defId);
    if (d?.cat === 'storage') out[it.loc.id] = (out[it.loc.id] ?? 0) + stat(d, 'capacityBbl', 0);
  }
  return out;
}

export function fieldTanks(state, fieldId) {
  return Object.values(state.items).filter((it) => it.loc.type === 'field' && it.loc.id === fieldId && eqDef(it.defId)?.cat === 'storage');
}

// Add oil to a field's storage: tanks first, then empty barrels from the warehouse, rest spills.
export function storeOil(state, fieldId, bbl) {
  const f = state.fields[fieldId];
  const s = f.storage;
  const tankOil = s.bbl - s.barrels;
  const free = Math.max(0, s.capacityBbl - tankOil);
  let rest = bbl;
  const toTank = Math.min(rest, free);
  s.bbl += toTank; rest -= toTank;
  if (rest > 0) {
    const bdef = barrelDef();
    // auto-buy barrels to catch production — only while the oil is worth more than the barrel
    if (bdef && f.autoBuyBarrels !== false) {
      const need = Math.ceil(rest - stockQty(state, bdef.id));
      const price = unitPrice(state, bdef);
      // (perf: the netback and hauling cap — pure reads — are only evaluated when barrels are actually needed)
      if (need > 0 && f.storage.barrels < Math.max(200, transportCapacity(state, fieldId) * 30) && netback(state, fieldId).cash > price) { // don't hoard more than ~a month of hauling
        const buy = Math.min(need, Math.floor(Math.max(0, state.company.cash) / price));
        if (buy > 0) { spend(state, buy * price, 'consumables'); addStock(state, bdef.id, buy); }
      }
    }
    const n = Math.min(Math.floor(rest + 0.999), bdef ? Math.floor(stockQty(state, bdef.id)) : 0);
    if (n > 0) {
      const amt = Math.min(rest, n);
      takeStock(state, bdef.id, Math.ceil(amt - 1e-9));
      s.barrels += Math.ceil(amt - 1e-9);
      s.bbl += amt; rest -= amt;
    }
  }
  if (rest > 1e-6 && yf(state) >= 2000) rest = m4Overflow(state, fieldId, rest); // M4-B: full storage on a negative-price day → forced sale
  if (rest > 1e-6) {
    f.spilledToday = (f.spilledToday ?? 0) + rest;
    state.company.stats.bblSpilled += rest;
  }
  return bbl - rest;
}

export function storageView(state, fieldId) {
  const f = state.fields[fieldId];
  const bdef = barrelDef();
  const empties = bdef ? stockQty(state, bdef.id) : 0;
  return { bbl: f.storage.bbl, tankCap: f.storage.capacityBbl, barrels: f.storage.barrels, emptyBarrels: empties, capacity: f.storage.capacityBbl + f.storage.barrels + empties };
}

// ---- transport & netback ----------------------------------------------------
export function transportDef(state, fieldId) {
  const f = state.fields[fieldId];
  return f.transportId ? eqDef(f.transportId) : null;
}

export function transportCapacity(state, fieldId) {
  const tdef = transportDef(state, fieldId);
  if (!tdef) return 0;
  if (m3TransportBlocked(state, fieldId, tdef)) return 0; // M3 ops: outlet not open yet / storm downtime
  const ts = transportStats(tdef);
  if (ts.perTeamster) {
    const teamsters = Object.values(state.crew).filter((c) => c.role === 'teamster' && c.status === 'active' && !c.injuredUntil).length;
    return ts.capacityPerDay * (CONTRACT_TEAMSTERS + teamsters);
  }
  return ts.capacityPerDay;
}

export function transportCostPerBbl(state, fieldId) {
  const tdef = transportDef(state, fieldId);
  if (!tdef) return 0;
  const ts = transportStats(tdef);
  let c = ts.costPerBbl;
  if (ts.perTeamster && ts.costRange) {
    const teamsters = Object.values(state.crew).filter((x) => x.role === 'teamster' && x.status === 'active').length;
    // contract carters charge the top of the range; your own teamsters haul at the bottom (plus wages)
    // boom-town carters: wagons are scarce in 1859-60 (top of the range), easing to the normal rate by 1863
    const scarce = clamp((yf(state) - 1860) / 3, 0, 1);
    const contract = ts.costRange[1] + (ts.costPerBbl - ts.costRange[1]) * scarce;
    c = teamsters > 0 ? ts.costRange[0] : contract;
  }
  const { month } = dayToDate(state.day);
  if (month === 3 || month === 4) c *= ts.mudSeasonMult;
  c *= transportMult(state, fieldId, tdef); // M2: U-boat tanker rates ×3 (1942-43), Big Inch Texas pipelines ×0.7
  c *= m3TariffMult(state, fieldId, tdef); // M3 ops: consortium members' tariff discount
  return eraDollars(state, c, tdef.baseYear ?? tdef.year ?? 1859);
}

// Region shipping cost to world markets ($/bbl, era $) — DESIGN §12.
export function regionShipping(state, fieldId) {
  const fdef = fieldDef(fieldId);
  const rdef = regionDef(fdef?.regionId);
  const m3 = m3RegionShipping(state, fieldId); // M3 econ: terminal → market route freight replaces the flat M2 ocean freight
  if (m3 != null) return m3;
  return rdef?.transportToMarket ? eraDollars(state, rdef.transportToMarket, 1859) * regionFreightMult(state) : 0; // M2: tanker freight curve
}

export function qualityMult(state, fieldId) {
  const m = netPriceMult(state, fieldId, state.fields[fieldId]?.transportId ?? null);
  return state.fields[fieldId]?.condShare ? m * condensateMult(state, fieldId) : m;
}

// Field royalty: production-weighted average of the landowner royalties on your producing plots
// (lease fever pushes new leases toward 1/2 royalty — see survey.leaseRoyalty).
export function royaltyRate(state, fieldId) {
  const m3r = m3RoyaltyRate(state, fieldId); // M3 econ: GOVERNMENT_TAKE royalty (posted basis) replaces the M2 schedule
  if (m3r != null) return m3r;
  const sched = scheduledRoyalty(fieldDef(fieldId)?.regionId, yf(state)); // M2: concession royalty schedules override
  if (sched != null) return sched;
  const f = state.fields[fieldId];
  if (f?.royaltyNow != null) return f.royaltyNow;
  const fdef = fieldDef(fieldId);
  return fdef?.royalty ?? regionDef(fdef?.regionId)?.royalty ?? 0.125;
}

// Thin local market (DESIGN §0 flavour: in 1859 a handful of Titusville buyers): selling q bbl in a day
// into a region of depth D fetches an average of spot × D/q × ln(1 + q/D). Depth grows as refineries,
// railroads and pipelines arrive. Barrels you hold can be fed out slowly at better prices.
const DEPTH_KEYS = [[1859.5, 10], [1860, 20], [1860.5, 60], [1861, 200], [1862, 800], [1864, 3000], [1866, 8000], [1875, 50000], [1900, 500000]];
export function marketDepth(state, fieldId) {
  const y = yf(state);
  const k = DEPTH_KEYS;
  let d;
  if (y <= k[0][0]) d = k[0][1];
  else if (y >= k[k.length - 1][0]) d = k[k.length - 1][1] * Math.max(1, (y - 1900) / 5);
  else for (let i = 1; i < k.length; i++) if (y <= k[i][0]) { const t = (y - k[i - 1][0]) / (k[i][0] - k[i - 1][0]); d = Math.exp(Math.log(k[i - 1][1]) + t * (Math.log(k[i][1]) - Math.log(k[i - 1][1]))); break; }
  return d;
}

function regionKey(fieldId) { return fieldDef(fieldId)?.regionId ?? fieldId; }

function soldToday(state, fieldId) {
  const st = state.salesToday;
  return st && st.day === state.day ? st.byRegion[regionKey(fieldId)] ?? 0 : 0;
}

// Average price multiplier for selling `q` more bbl today from this field's region.
export function depthMult(state, fieldId, q) {
  const D = marketDepth(state, fieldId);
  const q0 = soldToday(state, fieldId);
  if (q <= 1e-9) return 1 / (1 + q0 / D);
  return (D / q) * (Math.log(1 + (q0 + q) / D) - Math.log(1 + q0 / D));
}

export function netback(state, fieldId, q = 1, parts = null) {
  const P = parts ?? netbackParts(state, fieldId);
  const s0 = m4FieldSpot(state, fieldId); // M4-B: the field's hub price from 2000 (= spotPrice before)
  const spot = s0 * (s0 > 0 ? depthMult(state, fieldId, q) : 1);
  const gross = spot * P.quality;
  const royalty = Math.max(0, gross) * P.royaltyRate;
  const severance = Math.max(0, gross) * P.severanceRate; // M2 state production taxes (0 before 1905)
  const { transport, region, barrel } = P;
  return { spot, gross, royalty, severance, transport, region, barrel, net: gross - royalty - severance - transport - region - barrel, cash: gross - royalty - severance - transport - region };
}
// The q-independent terms of netback (all pure reads). tickSales computes them once per field and passes them to
// both its own netback and shipAndSell's (nothing they read changes in between) — same numbers, half the work.
export function netbackParts(state, fieldId) {
  const quality = worldQuality(state, fieldId, qualityMult(state, fieldId)); // M4-C: sanctions discount, price caps, syncrude
  const royaltyRate_ = royaltyRate(state, fieldId);
  const severanceRate = severanceFor(state, fieldId);
  const transport = transportCostPerBbl(state, fieldId);
  const region = regionShipping(state, fieldId);
  const tdef = transportDef(state, fieldId);
  const bdef = barrelDef();
  // the buyer refunds half the barrel (returnable cooperage), so a shipped barrel costs half its price
  const barrel = tdef && transportStats(tdef).needsBarrels && bdef ? unitPrice(state, bdef) * (1 - BARREL_REFUND) : 0;
  return { quality, royaltyRate: royaltyRate_, severanceRate, transport, region, barrel };
}

// Ship & sell bbl from a field right now (capacity checks done by caller). Returns bbl sold.
function shipAndSell(state, fieldId, want, parts = null) {
  const f = state.fields[fieldId];
  const s = f.storage;
  let bbl = Math.min(want, s.bbl);
  if (bbl <= 1e-6) return 0;
  const tdef = transportDef(state, fieldId);
  if (!tdef) return 0;
  if (saleBlocked(state, fieldId)) return 0; // M3 econ: embargo / sanctions / every route interrupted
  if (worldSaleBlocked(state, fieldId)) return 0; // M4-C: bitumen / extra-heavy crude needs diluent or an upgrader
  const ts = transportStats(tdef);
  const bdef = barrelDef();
  if (ts.needsBarrels && bdef) {
    // oil already in barrels ships first; tank oil needs empty barrels
    const fromBarrels = Math.min(bbl, s.barrels);
    let fromTank = bbl - fromBarrels;
    if (fromTank > 0) {
      const need = Math.ceil(fromTank - 1e-9);
      let have = Math.floor(stockQty(state, bdef.id));
      if (have < need && f.autoBuyBarrels !== false) {
        const price = unitPrice(state, bdef);
        const buy = Math.min(need - have, Math.floor(Math.max(0, state.company.cash) / price));
        if (buy > 0) { spend(state, buy * price, 'consumables'); addStock(state, bdef.id, buy); have += buy; }
      }
      fromTank = Math.min(fromTank, have);
      if (fromTank > 0) takeStock(state, bdef.id, Math.ceil(fromTank - 1e-9));
    }
    s.barrels -= Math.ceil(fromBarrels - 1e-9);
    s.barrels = Math.max(0, s.barrels);
    bbl = fromBarrels + fromTank;
  }
  if (bbl <= 1e-6) return 0;
  s.bbl = Math.max(0, s.bbl - bbl);
  f.shippedToday = (f.shippedToday ?? 0) + bbl;
  if (s.barrels > s.bbl) s.barrels = Math.floor(s.bbl);
  let delivered = bbl;
  if (f.theftUntil > state.day) delivered *= 1 - f.theftPct;
  delivered *= deliveredMult(state, fieldId, tdef); // M2: U-boat losses on coastal tankers
  const nb = netback(state, fieldId, delivered, parts);
  const gross = delivered * nb.gross;
  if (!state.salesToday || state.salesToday.day !== state.day) state.salesToday = { day: state.day, byRegion: {} };
  state.salesToday.byRegion[regionKey(fieldId)] = (state.salesToday.byRegion[regionKey(fieldId)] ?? 0) + delivered;
  earn(state, gross, 'revenue');
  spend(state, gross > 0 ? gross * royaltyRate(state, fieldId) : 0, 'royalties', false);
  onSale(state, fieldId, gross); // M2: severance, local partner's share
  onSaleM3(state, fieldId, delivered, gross, nb, opexPerWell(yf(state))); // M3 econ: host tax, windfall tax, tanker casualties
  worldOnSale(state, fieldId, delivered, gross); // M4-C: diluent cost, trapped pesos
  spend(state, bbl * (nb.transport + nb.region), 'transport', false);
  if (ts.needsBarrels && bdef) { const refund = bbl * unitPrice(state, bdef) * BARREL_REFUND; state.company.cash += refund; book(state, 'consumables', -refund); }
  state.company.stats.bblSold += delivered;
  state.company.stats.revenueTotal += gross;
  return bbl;
}

// ---- daily ------------------------------------------------------------------
export function tickProduction(state) {
  const year = yf(state);
  const produced = {};
  const byRes = {};
  const caps = tankCapacities(state);
  const pst = platformStorageByField(state); // M3 ops: GBS/FPSO storage
  for (const f of Object.values(state.fields)) { f.spilledToday = 0; f.shippedToday = 0; f.storage.capacityBbl = (caps[f.id] ?? 0) + (pst ? pst.get(f.id) ?? 0 : 0); }

  for (const well of allWells(state)) {
    const res = resState(state, well);
    if (well.status === 'GUSHER' && well.gusher && res) {
      const flow = Math.min(res.remaining, well.gusher.rate * Math.max(0.2, res.pressure));
      res.remaining -= flow;
      const tank = state.fields[well.fieldId].storage.capacityBbl > 0;
      // M2 gushers (M2_SPEC §4.2): captured = what pits & tanks can hold; no free 30% without storage
      const catchable = flow * (tank || well.gusher.m2 ? 1 : 0.3);
      const stored = well.ownerOperatorId ? 0 : storeOil(state, well.fieldId, catchable); // contract well: the oil is the operator's
      well.gusher.captured += stored;
      well.gusher.lost += flow - stored;
      state.company.stats.bblProduced += stored;
      // gusher fire risk (M2 gushers: wellcontrol.js — gas cap, boiler spacing)
      if (!well.gusher.m2 && chance(state, 0.01)) {
        const rig = well.rigId ? state.rigs[well.rigId] : null;
        if (rig) rig.condition = Math.max(0, rig.condition - range(state, 20, 60));
        well.fire = { day: state.day, untilDay: state.day + 5 };
        notify(state, 'blowout', { wellId: well.id, fieldId: well.fieldId, pause: true, text: `${well.name}: the gusher caught fire!`, level: 'warn' });
      }
      continue;
    }
    if (well.status !== 'PRODUCING' || !well.prod || !res) continue;
    if (well.m4Loop) continue; // M4-C: SAGD well pairs produce in sim/m4/worldOilSands.js
    const p = well.prod;
    if (p.shutIn || p.forcedUntil > state.day) {
      p.rate = 0;
      res.shutInRegain = Math.min(0.05, (res.shutInRegain ?? 0) + 0.001);
      continue;
    }
    let q;
    if (p.shale) q = shaleRate(state, well, res); // M4-A: modified hyperbolic, plot-bench drainage, lift, frac hits, SWD curtailment
    else {
      q = flowRate(well, res);
      p.needsLift = res.pressure < LIFT_THRESHOLD;
      const rm = rateMult(state, well, res, res.pressure, p.needsLift);
      if (p.needsLift && !rm.liftOk) q *= 0.1;
      q *= rm.mult;
    }
    q = Math.max(0, Math.min(q, res.remaining));
    // ---- M2 hook: prorationing & hot oil (sim/m2/proration.js). Unprorated: { q, hot: 0, age: 1 }. Hot barrels are
    // sold to bootleggers inside prorate() (and already taken from the reservoir); the decline clock runs at the produced rate.
    q = m3ProdCap(state, well, q); // M3 ops: platform process cap / export mode / host (no-op for land wells)
    const pr = prorate(state, well, q, res);
    q = pr.q;
    res.remaining -= q;
    p.age += pr.age;
    p.rate = q;
    p.cum += q;
    produced[well.fieldId] = (produced[well.fieldId] ?? 0) + (p.shale ? shaleProduced(state, well, q) : q); // M4-A: only the crude goes to storage (gas/NGL/water booked on state.shale.fields)
    if (year >= 1901) ((byRes[well.fieldId] ??= {})[well.reservoirId] = (byRes[well.fieldId][well.reservoirId] ?? 0) + q);
    // opex & lift fuel
    const opex = opexPerWell(year) * (well.offshore ? offshoreWellOpexMult(well) : 1); // M3 ops: offshore opex (DESIGN §5.5)
    spend(state, opex, 'opex', false);
    well.cost += opex;
    if (well.lift) {
      const ldef = eqDef(well.lift.defId);
      const fuel = eraDollars(state, stat(ldef, 'fuelPerDay', 0), ldef?.year ?? 1859) / (well.lift.share ?? 1);
      if (fuel > 0) spend(state, fuel, 'fuel', false);
    }
    if (q < 0.2 && res.pressure < 0.3) {
      well.status = 'DEPLETED';
      p.rate = 0;
      notify(state, 'problem', { wellId: well.id, problem: 'DEPLETED', text: `${well.name} has played out. Plug it or shoot it.` });
    }
  }
  // production-weighted royalty per field
  const rq = {};
  for (const well of allWells(state)) {
    if (!well.prod?.rate) continue;
    const p = state.fields[well.fieldId].plots[well.plot];
    const r = p.royalty ?? fieldDef(well.fieldId)?.royalty ?? 0.125;
    (rq[well.fieldId] ??= [0, 0]);
    rq[well.fieldId][0] += well.prod.rate * r; rq[well.fieldId][1] += well.prod.rate;
  }
  for (const [fid, [a, b]] of Object.entries(rq)) if (b > 0) state.fields[fid].royaltyNow = a / b;
  // reservoir pressure
  const m2p = year >= 1901;
  for (const fk in state.fields) {
    const f = state.fields[fk], rs = f.reservoirs;
    for (const rid in rs) {
      const res = rs[rid];
      res.pressure = m2p ? reservoirPressure(f.id, rid, res) : clamp((res.initial > 0 ? res.remaining / res.initial : 0) + (res.shutInRegain ?? 0), 0, 1);
    }
  }
  if (year >= 1901) {
    for (const [fid, m] of Object.entries(byRes)) noteCondensate(state, fid, m);
    for (const [fid, q] of Object.entries(produced)) flareDay(state, fid, q);
  }
  // store production
  for (const [fid, q0] of Object.entries(produced)) {
    const q = equityBbl(state, fid, q0, opexPerWell(year), netback); // M3 econ: state participation / service-contract barrels
    const stored = storeOil(state, fid, q);
    state.company.stats.bblProduced += stored;
  }
  tickStorage(state);
  tickSales(state);
}

function spillNotice(state, f, rep = true) {
  if (!f.storageFullWarned) {
    f.storageFullWarned = true;
    notify(state, 'storageFull', { fieldId: f.id, pause: true, text: `Storage full at ${fieldDef(f.id)?.name} — oil is running into the creek! Buy barrels, build a tank, or sell.`, level: 'warn' });
  }
  if (rep && yearOf(state) >= 1970) state.company.reputation = Math.max(0, state.company.reputation - f.spilledToday / 1000);
}

function tickStorage(state) {
  // tanks by field in one pass (fieldTanks per field is O(items) × ~30 fields)
  const byField = {};
  for (const it of Object.values(state.items)) if (it.loc.type === 'field' && eqDef(it.defId)?.cat === 'storage') (byField[it.loc.id] ??= []).push(it);
  for (const f of Object.values(state.fields)) {
    const s = f.storage;
    if (s.bbl <= 0) { // nothing stored — but a well flowing into a field with no tanks at all is spilling every barrel
      if (f.spilledToday > 0) spillNotice(state, f, false); else f.storageFullWarned = false; // the notice only: no new reputation cost here
      continue;
    }
    // leaks
    const tankOil = s.bbl - s.barrels;
    let leak = 0;
    const tanks = byField[f.id] ?? [];
    if (tankOil > 0 && s.capacityBbl > 0) {
      for (const t of tanks) {
        const d = eqDef(t.defId);
        leak += tankOil * (stat(d, 'capacityBbl', 0) / s.capacityBbl) * stat(d, 'leakPerDay', 0);
      }
    }
    const bdef = barrelDef();
    leak += s.barrels * stat(bdef, 'leakPerDay', 0.0005);
    if (yf(state) >= 1901) leak += evaporation(state, f, tanks, tankOil);
    s.bbl = Math.max(0, s.bbl - leak);
    // lightning
    const lightning = fieldDef(f.id)?.hazards?.lightning ?? 1;
    maybeStormWarning(state, f, tanks);
    const storm = stormMult(state, f.id);
    for (const t of tanks) {
      if (f.tankFire) break; // one fire at a time; the player is deciding how to fight it
      const d = eqDef(t.defId);
      const p = stat(d, 'firePerDay', 0) * (lightning > 0.01 ? 1 : lightning / 0.0002) * tankFireMult(state, t) * storm;
      if (p > 0 && chance(state, p)) startTankFire(state, f, t);
    }
    // overflow notice
    if (f.spilledToday > 0) spillNotice(state, f);
    else {
      const room = s.capacityBbl + s.barrels + (bdef ? stockQty(state, bdef.id) : 0);
      if (room > 0 && s.bbl >= 0.8 * room && !f.storageNearWarned) {
        f.storageNearWarned = true;
        notify(state, 'storageFull', { fieldId: f.id, nearlyFull: true, pct: Math.round(100 * s.bbl / room), text: `Storage at ${fieldDef(f.id)?.name} is ${Math.round(100 * s.bbl / room)}% full.`, level: 'warn' });
      }
      if (s.bbl < 0.6 * room) { f.storageFullWarned = false; f.storageNearWarned = false; }
    }
  }
}

function tickSales(state) {
  for (const f of Object.values(state.fields)) {
    if (f.storage.bbl <= 1e-6 && f.shipQueue <= 0) continue;
    let cap = transportCapacity(state, f.id);
    if (cap <= 0) continue;
    // manual shipments queued first
    if (f.shipQueue > 0) {
      const sold = shipAndSell(state, f.id, Math.min(f.shipQueue, cap));
      f.shipQueue = Math.min(Math.max(0, f.shipQueue - sold), f.storage.bbl);
      if (sold <= 0) { // nothing could move — drop the order, but say why (players read silence as a bug)
        const why = shipBlockReason(state, f.id);
        notify(state, 'problem', { fieldId: f.id, problem: 'SHIP_FAILED', text: `${fieldDef(f.id)?.name}: shipment cancelled — ${why ?? 'nothing could be moved today'}.` });
        f.shipQueue = 0;
      }
      cap -= sold;
      if (f.shipQueue <= 0 && f.shipTimerId) { cancelTimer(state, f.shipTimerId); f.shipTimerId = null; }
    }
    if (f.sellMode.mode !== 'auto' || cap <= 0) continue;
    if (state.refining?.routes?.[f.id]) continue; // crude routed to a refinery (sim/refining.js pulls it)
    const parts = netbackParts(state, f.id);
    const nb = netback(state, f.id, 1, parts);
    // oil already in barrels only needs to beat haulage; tank oil also has to pay for a new barrel
    const inBarrels = f.storage.barrels >= Math.min(cap, f.storage.bbl) - 0.5;
    if ((inBarrels ? nb.cash : nb.net) <= 0) {
      if (!f.netbackWarned) {
        f.netbackWarned = true;
        notify(state, 'news', { fieldId: f.id, kind: 'market', text: `Netback at ${fieldDef(f.id)?.name} is negative (${fmt$(nb.net)}/bbl) — auto-sell paused. Hold the oil in storage.`, level: 'warn' });
      }
      continue;
    }
    f.netbackWarned = false;
    const want = Math.min(cap, f.storage.bbl * (f.sellMode.pct ?? 100) / 100);
    if (want > 0.01) shipAndSell(state, f.id, want, parts);
  }
}

// ---- actions ----------------------------------------------------------------
export function sellOilImpl(state, fieldId, bbl) {
  const f = state.fields[fieldId];
  if (!f) return { ok: false, msg: 'No such field.' };
  const cap = transportCapacity(state, fieldId);
  if (cap <= 0) {
    const tdef = transportDef(state, fieldId);
    const why = tdef ? (transportBlockReason(state, fieldId, tdef) ?? 'the route is shut today (storm or closure)') : 'no transport is set for this field';
    return { ok: false, msg: `Can't ship from ${fieldDef(fieldId)?.name}: ${why}.${betterTransportHint(state, fieldId, 0)}` };
  }
  const avail = Math.max(0, f.storage.bbl - f.shipQueue);
  const amt = Math.min(bbl, avail);
  if (amt <= 0) return { ok: false, msg: 'No oil in storage to sell.' };
  const blocked = shipBlockReason(state, fieldId);
  if (blocked) return { ok: false, msg: `Can't ship from ${fieldDef(fieldId)?.name}: ${blocked}.${betterTransportHint(state, fieldId, cap)}` };
  f.shipQueue += amt;
  const days = Math.ceil(f.shipQueue / cap);
  if (f.shipTimerId && state.timers[f.shipTimerId]) { const t = state.timers[f.shipTimerId]; t.daysLeft = days; t.totalDays = Math.max(t.totalDays, days); }
  else f.shipTimerId = startTimer(state, 'SHIPMENT', fieldId, days, { fieldId }).id;
  const span = days > 730 ? `about ${Math.round(days / 365)} years` : days > 60 ? `about ${Math.round(days / 30)} months` : `${days} day${days > 1 ? 's' : ''}`;
  return { ok: true, msg: `Shipping ${Math.round(amt).toLocaleString('en-US')} bbl — ${span} at ${Math.round(cap).toLocaleString('en-US')} bbl/day.${days > 60 ? betterTransportHint(state, fieldId, cap) : ''}` };
}

// SHIPMENT timer done: rushed → deliver whole queue now; natural end → queue normally empty already.
export function onShippingDone(state, t, rushed) {
  const f = state.fields[t.refId];
  if (!f) return;
  f.shipTimerId = null;
  if (f.shipQueue > 0) {
    if (rushed) { shipAndSell(state, f.id, f.shipQueue); f.shipQueue = 0; }
    else {
      const cap = transportCapacity(state, f.id) || 1;
      f.shipTimerId = startTimer(state, 'SHIPMENT', f.id, Math.ceil(f.shipQueue / cap), { fieldId: f.id }).id;
    }
  }
}

export function buildStorageImpl(state, fieldId, defId) {
  const def = eqDef(defId);
  const f = state.fields[fieldId];
  if (!f) return { ok: false, msg: 'No such field.' };
  if (!def || def.cat !== 'storage') return { ok: false, msg: 'Not a storage item.' };
  if (!isAvailable(def, state)) return { ok: false, msg: `${def.name} is not available yet.` };
  const blocked = regionBlock(state, fieldId); // M2: closed / occupied regions
  if (blocked) return { ok: false, msg: blocked };
  if (stat(def, 'consumable', false)) return { ok: false, msg: 'Barrels are bought in the shop.' };
  const price = unitPrice(state, def);
  if (!spend(state, price, 'capex')) return { ok: false, msg: `Need ${fmt$(price)}.` };
  addItem(state, defId, { type: 'field', id: fieldId });
  f.storage.capacityBbl = tankCapacity(state, fieldId);
  return { ok: true, msg: `${def.name} built at ${fieldDef(fieldId)?.name}.` };
}

export function setTransportImpl(state, fieldId, defId) {
  const f = state.fields[fieldId];
  const def = eqDef(defId);
  if (!f || !def || def.cat !== 'transport') return { ok: false, msg: 'Invalid transport.' };
  const fdef = fieldDef(fieldId);
  const allowed = fieldTransports(fdef).find((t) => t.id === defId);
  const y = yearOf(state);
  if (!allowed) return { ok: false, msg: `${def.name} doesn't serve ${fdef.name}.` };
  if (transportStats(def).requires === 'port' && yf(state) >= 1946 && !fieldHasPort(fdef)) return { ok: false, msg: `${fdef.name} has no port for tankers.` }; // M3 econ §18.11
  if (allowed.year > y || !isAvailable(def, state)) return { ok: false, msg: `${def.name} reaches ${fdef.name} in ${Math.max(allowed.year, def.year)}.` };
  const m3b = yf(state) >= 1946 ? transportBlockReason(state, fieldId, def) : null; // M3 ops: megaproject not open yet
  if (m3b) return { ok: false, msg: m3b };
  if (!f.connected.includes(defId)) {
    const ts = transportStats(def);
    const cost = eraDollars(state, ts.connectCost, def.baseYear ?? def.year) || (def.basePrice ? unitPrice(state, def) : 0);
    if (cost > 0 && !spend(state, cost, 'capex')) return { ok: false, msg: `Connecting costs ${fmt$(cost)}.` };
    f.connected.push(defId);
  }
  f.transportId = defId;
  return { ok: true, msg: `${fdef.name} now ships by ${def.name}.` };
}

export function installLiftImpl(state, wellId, defId) {
  const well = state.wells[wellId];
  const def = eqDef(defId);
  if (!well || !def || def.cat !== 'lift') return { ok: false, msg: 'Invalid lift.' };
  if (!['PRODUCING', 'SHUT_IN', 'DEPLETED'].includes(well.status) || !well.prod) return { ok: false, msg: 'Only completed wells can be pumped.' };
  if (!isAvailable(def, state)) return { ok: false, msg: `${def.name} not available until ${def.year}.` };
  if (well.lift) return { ok: false, msg: 'Well already has a pump.' };
  if (stat(def, 'tiesUpRig', false)) {
    let rig = well.rigId ? state.rigs[well.rigId] : null;
    if (!rig) rig = Object.values(state.rigs).find((r) => !r.wellId);
    if (!rig) return { ok: false, msg: 'Walking-beam pumping needs an idle rig on the well.' };
    rig.at = { fieldId: well.fieldId, plot: well.plot };
    rig.wellId = well.id;
    rig.task = { type: 'PUMPING' };
    well.rigId = rig.uid;
    well.lift = { defId, uid: null, usesRig: true, share: 1 };
  } else if (!stat(def, 'perWell', true)) {
    const max = stat(def, 'maxWells', 8);
    let plant = Object.values(state.items).find((i) => i.defId === defId && i.loc.type === 'field' && i.loc.id === well.fieldId && (i.wells?.length ?? 0) < max);
    if (!plant) {
      const price = unitPrice(state, def);
      if (!spend(state, price, 'capex')) return { ok: false, msg: `Need ${fmt$(price)}.` };
      plant = addItem(state, defId, { type: 'field', id: well.fieldId });
      plant.wells = [];
    }
    plant.wells.push(well.id);
    well.lift = { defId, uid: plant.uid, usesRig: false, share: plant.wells.length };
    for (const wid of plant.wells) if (state.wells[wid]?.lift) state.wells[wid].lift.share = plant.wells.length;
  } else {
    const price = unitPrice(state, def);
    if (!spend(state, price, 'capex')) return { ok: false, msg: `Need ${fmt$(price)}.` };
    const it = addItem(state, defId, { type: 'well', id: well.id });
    well.lift = { defId, uid: it.uid, usesRig: false, share: 1 };
  }
  if (well.status === 'DEPLETED') well.status = 'PRODUCING';
  return { ok: true, msg: `${def.name} pumping ${well.name}.` };
}

export function shutInImpl(state, wellId, on) {
  const well = state.wells[wellId];
  if (!well?.prod || !['PRODUCING', 'SHUT_IN'].includes(well.status)) return { ok: false, msg: 'Only producing wells can be shut in.' };
  well.prod.shutIn = !!on;
  well.status = on ? 'SHUT_IN' : 'PRODUCING';
  if (on) well.prod.rate = 0;
  return { ok: true, msg: `${well.name} ${on ? 'shut in' : 'back on production'}.` };
}

export function setSellModeImpl(state, fieldId, mode, pct = 100) {
  const f = state.fields[fieldId];
  if (!f) return { ok: false, msg: 'No such field.' };
  if (!['auto', 'hold', 'manual'].includes(mode)) return { ok: false, msg: 'Mode must be auto, hold or manual.' };
  f.sellMode = { mode, pct: clamp(pct ?? 100, 1, 100) };
  // Hold means hold now: oil already queued to ship (Sell / Sell all) stays in the tanks too
  let cancelled = 0;
  if (mode === 'hold' && f.shipQueue > 0) { cancelled = f.shipQueue; f.shipQueue = 0; if (f.shipTimerId) { cancelTimer(state, f.shipTimerId); f.shipTimerId = null; } }
  return { ok: true, msg: mode === 'auto' ? `Auto-selling ${f.sellMode.pct}% at spot.` : mode === 'hold' ? `Holding oil in storage${cancelled >= 1 ? ` — ${Math.round(cancelled).toLocaleString('en-US')} bbl queued for sale kept back` : ''}.` : 'Manual selling.' };
}

// Why oil can't leave a field right now (null = it can). Used to explain refusals instead of failing silently.
export function shipBlockReason(state, fieldId) {
  const f = state.fields[fieldId];
  const tdef = transportDef(state, fieldId);
  if (!tdef) return 'no transport is set for this field';
  const sb = saleBlocked(state, fieldId);
  if (sb) return typeof sb === 'string' ? sb : (sb.reason ?? sb.text ?? 'every route to market is blocked (embargo, sanctions or war)');
  const wb = worldSaleBlocked(state, fieldId); // M4-C: diluent / upgrader requirement
  if (wb) return wb;
  const ts = transportStats(tdef);
  const bdef = barrelDef();
  if (ts.needsBarrels && bdef && f) {
    const inBarrels = f.storage.barrels ?? 0;
    const spare = Math.floor(stockQty(state, bdef.id));
    const price = unitPrice(state, bdef);
    const affordable = price > 0 ? Math.floor(Math.max(0, state.company.cash) / price) : Infinity;
    if (inBarrels + spare + affordable < 1) return `${tdef.name} needs empty barrels (${fmt$(price)} each) and you have none and no cash to buy them`;
  }
  return null;
}

// "Switch to X" advice when the field has a much bigger outlet available than the one in use.
export function betterTransportHint(state, fieldId, cap) {
  const fdef = fieldDef(fieldId);
  const year = yearOf(state);
  const cur = state.fields[fieldId]?.transportId;
  let best = null;
  for (const t of fieldTransports(fdef)) {
    if (t.id === cur || (t.year && year < t.year)) continue;
    const d = eqDef(t.id);
    if (!d || !isAvailable(d, state)) continue;
    const c = transportStats(d).capacityPerDay ?? 0;
    if (!best || c > best.c) best = { d, c };
  }
  if (!best || best.c <= Math.max(1, cap) * 2) return '';
  return ` Tip: switch this field to ${best.d.name} (${Math.round(best.c).toLocaleString('en-US')} bbl/day${transportStats(best.d).needsBarrels ? '' : ', no barrels needed'}) in Field → Transport to market.`;
}
